import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Heart, 
  Eye, 
  Brain, 
  Bone, 
  Baby, 
  Stethoscope,
  Smile,
  Activity,
  Syringe,
  Pill
} from 'lucide-react';
import { specialtyService, transformSpecialty } from '@/services/api';

// Mock data for when API is unavailable
const getMockSpecialties = (lang) => [
  { id: 1, name: lang === 'ar' ? 'أمراض القلب' : 'Cardiology', description: lang === 'ar' ? 'أخصائيو القلب والأوعية الدموية' : 'Heart and cardiovascular system specialists', doctors_count: 24 },
  { id: 2, name: lang === 'ar' ? 'طب العيون' : 'Ophthalmology', description: lang === 'ar' ? 'أخصائيو العناية بالعيون والرؤية' : 'Eye care and vision specialists', doctors_count: 18 },
  { id: 3, name: lang === 'ar' ? 'طب الأعصاب' : 'Neurology', description: lang === 'ar' ? 'أخصائيو الدماغ والجهاز العصبي' : 'Brain and nervous system specialists', doctors_count: 15 },
  { id: 4, name: lang === 'ar' ? 'جراحة العظام' : 'Orthopedics', description: lang === 'ar' ? 'أخصائيو العظام والمفاصل' : 'Bone and joint specialists', doctors_count: 20 },
  { id: 5, name: lang === 'ar' ? 'طب الأطفال' : 'Pediatrics', description: lang === 'ar' ? 'أخصائيو صحة الأطفال' : 'Children healthcare specialists', doctors_count: 22 },
  { id: 6, name: lang === 'ar' ? 'الطب العام' : 'General Medicine', description: lang === 'ar' ? 'أطباء الرعاية الأولية' : 'Primary care physicians', doctors_count: 30 },
  { id: 7, name: lang === 'ar' ? 'طب الأسنان' : 'Dentistry', description: lang === 'ar' ? 'أخصائيو صحة الفم والأسنان' : 'Dental and oral health specialists', doctors_count: 28 },
  { id: 8, name: lang === 'ar' ? 'الأمراض الجلدية' : 'Dermatology', description: lang === 'ar' ? 'أخصائيو العناية بالبشرة' : 'Skin care specialists', doctors_count: 16 },
  { id: 9, name: lang === 'ar' ? 'أمراض النساء' : 'Gynecology', description: lang === 'ar' ? 'أخصائيو صحة المرأة' : 'Women health specialists', doctors_count: 19 },
];

const SpecialtiesPage = () => {
  const { t, i18n } = useTranslation();
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);

  const lang = i18n.language || 'ar';

  const getIconForSpecialty = (name) => {
    const nameLower = name?.toLowerCase() || '';
    if (nameLower.includes('قلب') || nameLower.includes('cardio') || nameLower.includes('heart')) return Heart;
    if (nameLower.includes('عيون') || nameLower.includes('ophthal') || nameLower.includes('eye')) return Eye;
    if (nameLower.includes('عصب') || nameLower.includes('neuro') || nameLower.includes('brain')) return Brain;
    if (nameLower.includes('عظم') || nameLower.includes('ortho') || nameLower.includes('bone')) return Bone;
    if (nameLower.includes('أطفال') || nameLower.includes('pediatr') || nameLower.includes('child')) return Baby;
    if (nameLower.includes('أسنان') || nameLower.includes('dent') || nameLower.includes('tooth')) return Smile;
    if (nameLower.includes('جلد') || nameLower.includes('derma') || nameLower.includes('skin')) return Activity;
    if (nameLower.includes('نسا') || nameLower.includes('gyne') || nameLower.includes('women')) return Syringe;
    if (nameLower.includes('جراح') || nameLower.includes('surg')) return Stethoscope;
    if (nameLower.includes('عام') || nameLower.includes('general')) return Stethoscope;
    return Pill;
  };

  const getColorForSpecialty = (name) => {
    const nameLower = name?.toLowerCase() || '';
    if (nameLower.includes('قلب') || nameLower.includes('cardio')) return 'bg-red-50 text-red-600';
    if (nameLower.includes('عيون') || nameLower.includes('ophthal')) return 'bg-blue-50 text-blue-600';
    if (nameLower.includes('عصب') || nameLower.includes('neuro')) return 'bg-purple-50 text-purple-600';
    if (nameLower.includes('عظم') || nameLower.includes('ortho')) return 'bg-orange-50 text-orange-600';
    if (nameLower.includes('أطفال') || nameLower.includes('pediatr')) return 'bg-pink-50 text-pink-600';
    if (nameLower.includes('عام') || nameLower.includes('general')) return 'bg-teal-50 text-teal-600';
    if (nameLower.includes('أسنان') || nameLower.includes('dent')) return 'bg-cyan-50 text-cyan-600';
    if (nameLower.includes('جلد') || nameLower.includes('derma')) return 'bg-amber-50 text-amber-600';
    if (nameLower.includes('نسا') || nameLower.includes('gyne')) return 'bg-rose-50 text-rose-600';
    if (nameLower.includes('جراح') || nameLower.includes('surg')) return 'bg-indigo-50 text-indigo-600';
    return 'bg-slate-50 text-slate-600';
  };

  useEffect(() => {
    const fetchSpecialties = async () => {
      setLoading(true);
      try {
        const response = await specialtyService.getAll();
        const rawSpecialties = response.data.data || response.data || [];
        if (rawSpecialties.length > 0) {
          setSpecialties(rawSpecialties.map(s => transformSpecialty(s, lang)));
        } else {
          setSpecialties(getMockSpecialties(lang));
        }
      } catch (error) {
        console.error('Failed to fetch specialties, using mock data:', error);
        setSpecialties(getMockSpecialties(lang));
      } finally {
        setLoading(false);
      }
    };
    fetchSpecialties();
  }, [lang]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="specialties-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{t('nav.specialties')}</h1>
          <p className="text-teal-100 max-w-2xl">
            {lang === 'ar' 
              ? 'تصفح تخصصاتنا الطبية واعثر على الأخصائي المناسب لاحتياجاتك'
              : 'Browse our medical specialties and find the right healthcare professional for your needs'
            }
          </p>
        </div>
      </div>

      {/* Specialties Grid */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Skeleton className="h-14 w-14 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-1/2" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specialties.map((specialty) => {
              const Icon = getIconForSpecialty(specialty.name);
              const colorClass = getColorForSpecialty(specialty.name);
              
              return (
                <Link 
                  key={specialty.id} 
                  to={`/doctors?specialty=${specialty.id}`}
                  className="group"
                  data-testid={`specialty-card-${specialty.id}`}
                >
                  <Card className="dark:bg-slate-800 border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 rounded-xl ${colorClass} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                          <Icon className="h-7 w-7" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-teal-600 transition-colors">
                            {specialty.name}
                          </h3>
                          {/* Render description as HTML since API returns HTML content */}
                          <div 
                            className="text-sm text-slate-500 mb-2 line-clamp-2"
                            dangerouslySetInnerHTML={{ 
                              __html: specialty.description || `${specialty.name} specialists` 
                            }}
                          />
                          {specialty.doctors_count > 0 && (
                            <span className="text-xs font-medium text-teal-600">
                              {specialty.doctors_count} {t('nav.doctors')}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecialtiesPage;
